import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import axios from "axios";
import EditProfilePictureModal from "./edit_pp"; // Make sure to import your modal component
import SuccessNotification from "../../components/SuccessNotification";
import ErrorNotification from "../../components/ErrorNotification";

function EditCompanyProfile() {
  const [company, setCompany] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
  const [file, setFile] = useState(null); // State to hold the file
  const [currentUser, setCurrentUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState(null);
  const [successNotification, setSuccessNotification] = useState({ isVisible: false, message: "" });
  const [errorNotification, setErrorNotification] = useState({ isVisible: false, message: "" });
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/signin");
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get("/api/companies/fetchCompanyData", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const companyData = response.data.companyData;

        setCompany(companyData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle hash fragment for scrolling to invite section
  useEffect(() => {
    if (window.location.hash === '#invite-section') {
      // Wait for the page to load and then scroll to the invite section
      setTimeout(() => {
        const inviteSection = document.getElementById('invite-section');
        if (inviteSection) {
          inviteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, []);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const { data } = await axios.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Current user data:", data.user);
        console.log("User role:", data.user.role);
        setCurrentUser(data.user);
      } catch (err) {
        console.error("Failed to fetch current user", err);
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      // For legacy users, we might not have currentUser, so check if we have a token instead
      const token = localStorage.getItem("token");
      if (!token) {
        setTeamError("Authentication token not found.");
        setTeamLoading(false);
        return;
      }

      setTeamLoading(true);
      setTeamError(null);
      try {
        const { data } = await axios.get("/api/company/fetchTeamMembers", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTeamMembers(data.teamMembers);
      } catch (err) {
        console.error("Failed to fetch team members", err);
        setTeamError(err.response?.data?.message || "Could not load team members.");
      } finally {
        setTeamLoading(false);
      }
    };

    fetchTeamMembers();
  }, [currentUser]); // Re-run when currentUser is fetched (or if it fails)

  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    website: "",
    email: "",
    linkedin: "",
    description: "",
    file: null,
    equity: "",
    funding: "",
    location: "",
    email: "",
    profile_picture_link: "",
    team_size: "",
  });

  useEffect(() => {
    if (company) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        name: company.name || "",
        website: company.website || "",
        linkedin: company.linkedin || "",
        description: company.description || "",
        equity: company.equity || "",
        funding: company.funding || "",
        profile_picture_link: company.logo,
        location: company.location || "",
        email: company.email || "",
        team_size: company.team_size || "",
      }));
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const updatedData = {
      fields: {
        "Startup Name": formData.name,
        Website: formData.website,
        "Startup Description": formData.description,
        "Team Size": formData.team_size,
        Location: formData.location,
        ...(formData.funding && formData.funding !== "Choose an option"
          ? { "Funding Stage": formData.funding }
          : {}),
      },
    };

    if (formData.profile_picture_link)
      updatedData.fields["Logo"] = [{ url: formData.profile_picture_link }];

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authorization token is missing");
      }

      const response = await axios.patch(
        "/api/companies/updateCompanyData",
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json", // Ensure this matches the server expectation
          },
        }
      );
      // Handle successful update
    } catch (err) {
      console.error("Error updating company data:", err);
      setError("Failed to update company data: " + err.message);
    }
  };

  const handleInvite = async () => {
    setInviteLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Missing token");
      
      // Debug: Check what's in the token
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        console.log("Token payload:", tokenPayload);
        console.log("CompanyId from token:", tokenPayload.companyId);
        console.log("User role from token:", tokenPayload.role);
      } catch (tokenError) {
        console.error("Error parsing token:", tokenError);
      }
      
      const response = await axios.post(
        "/api/company/invite",
        { 
          email: inviteEmail,
          name: inviteName 
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      const { companyName } = response.data;
      setSuccessNotification({
        isVisible: true,
        message: `${inviteName || inviteEmail} will receive an email to join ${companyName}.`
      });
      setInviteEmail("");
      setInviteName("");
      
      // Refresh the team members list after successful invite
      const { data } = await axios.get("/api/company/fetchTeamMembers", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamMembers(data.teamMembers);
    } catch (err) {
      console.error("Invite failed", err);
      console.error("Full error details:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      let errorMessage = "Failed to send invitation";
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        
        // Provide more helpful error messages
        if (err.response.data.message === "No companyId in token") {
          // Check if this is due to old token format
          try {
            const tokenPayload = JSON.parse(atob(localStorage.getItem("token").split('.')[1]));
            if (!tokenPayload.companyId && !tokenPayload.role) {
              errorMessage = "Your session is outdated. Please log out and log back in to invite team members.";
            } else {
              errorMessage = "Your account is not associated with a company. Please contact support.";
            }
          } catch (tokenError) {
            errorMessage = "Your session is invalid. Please log out and log back in to invite team members.";
          }
        } else if (err.response.data.message === "Only admin can invite") {
          errorMessage = "Only administrators can invite team members. Please contact your admin.";
        }
      }
      
      setErrorNotification({
        isVisible: true,
        message: errorMessage
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const CompanyLogo = ({ headerImage }) => {
    const profileFallback =
      "https://via.placeholder.com/600x400.png?text=No+Image+Available"; // Update this to your actual fallback image path

    return (
      <img
        src={headerImage || profileFallback}
        alt="profile"
        className="h-[300px] w-[400px] object-cover rounded-lg bg-[var(--overlay-2)]"
        onError={(e) => {
          e.target.src = profileFallback; // Provide a fallback source
          console.error("Failed to load image at", e.target.src);
        }}
      />
    );
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/passwordless-login");
  };

  const handleFileUpload = (url) => {
    setFormData({ ...formData, file: url });
    setCompany({ ...company, logo: url });
  };

  return (
    <div className="flex flex-col pt-10 px-4 md:pl-72 items-left bg-white"> {/* mobile-only tweak: added px-4 for mobile padding, pl-72 only on md+ */}
      <div className="max-w-6xl w-full flex flex-col h-full bg-white">
        <form onSubmit={handleSubmit}>
          <div className="space-y-12">
            <div className=" border-gray-900/10 pb-4">
              <h2
                className="text-2xl md:text-4xl font-semibold leading-7 text-gray-900" /* mobile-only tweak: smaller font on mobile */
                style={{ color: "rgb(0, 185, 140)" }}
              >
                {company.name}
              </h2>
              <div className="col-span-full mt-4">
                <h2
                  htmlFor="photo"
                  className="block text-xl md:text-3xl font-light leading-6 text-gray-900" /* mobile-only tweak: smaller font on mobile */
                >
                  Company Image
                </h2>
                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-x-3"> {/* mobile-only tweak: stack vertically on mobile, horizontal on sm+ */}
                  <div className="w-full sm:w-auto flex justify-center sm:justify-start"> {/* mobile-only tweak: center image on mobile */}
                    <img
                      src={company.logo || "https://via.placeholder.com/600x400.png?text=No+Image+Available"}
                      alt="profile"
                      className="h-48 w-64 sm:h-[300px] sm:w-[400px] object-cover rounded-lg bg-[var(--overlay-2)]" /* mobile-only tweak: smaller image on mobile */
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/600x400.png?text=No+Image+Available";
                        console.error("Failed to load image at", e.target.src);
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={openModal}
                    className="px-4 py-3 text-sm text-black flex rounded-lg font-medium transform bg-white hover:bg-[#02B491] hover:text-white border-2 border-[#02B491] w-full sm:w-auto justify-center" /* mobile-only tweak: full width on mobile, auto on sm+, better padding for touch */
                  >
                    Edit Profile Picture
                  </button>
                </div>
              </div>
              <h2 className="block mt-8 text-xl md:text-3xl font-light leading-6 text-gray-900"> {/* mobile-only tweak: smaller font on mobile */}
                Company Information
              </h2>
              <div className="mt-6 sm:col-span-8 mt-4 flex flex-col md:flex-row md:space-x-8 space-y-6 md:space-y-0"> {/* mobile-only tweak: stack vertically on mobile, horizontal on md+ */}
                <div className="w-full md:w-2/5"> {/* mobile-only tweak: full width on mobile, 2/5 on md+ */}
                  <div className="mb-4">
                    <label
                      htmlFor="name"
                      className="block text-md font-semibold leading-6 text-gray-900"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      autoComplete="name"
                      className="block pl-2 mt-2 w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-md sm:leading-6"
                      placeholder="Jumpstart"
                      value={formData.name}
                      onChange={handleChange}
                    ></input>
                    {formErrors.name && (
                      <p className="text-red-600 text-md">{formErrors.name}</p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="company"
                      className="block text-md font-semibold leading-6 text-gray-900"
                    >
                      Website
                    </label>
                    <input
                      type="text"
                      name="website"
                      id="website"
                      className="block mt-2 pl-2 w-full rounded-md border-0 py-1.5 text-gray-900  ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-md sm:leading-6"
                      placeholder="https://www.jumpstart-uk.com/"
                      value={formData.website}
                      onChange={handleChange}
                    ></input>
                    {formErrors.linkedin && (
                      <p className="text-red-600 text-md">
                        {formErrors.website}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="city"
                      className="block text-md font-semibold leading-6 text-gray-900"
                    >
                      Location
                    </label>
                    <div>
                      <input
                        type="text"
                        name="location"
                        id="location"
                        placeholder="North London"
                        autoComplete="address-level2"
                        value={formData.location}
                        onChange={handleChange}
                        className="block pl-2 mt-2 w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-md sm:leading-6"
                      ></input>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="team-size"
                      className="block text-md font-semibold leading-6 text-gray-900"
                    >
                      Team Size
                    </label>
                    <div>
                      <input
                        type="number"
                        name="team_size"
                        id="team_size"
                        placeholder="6"
                        autoComplete="team_size"
                        value={formData.team_size}
                        onChange={handleChange}
                        className="block pl-2 mt-2 w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-md sm:leading-6"
                      ></input>
                    </div>
                  </div>
                </div>
                <div className="w-full md:flex-1"> {/* mobile-only tweak: full width on mobile, flex-1 on md+ */}
                  <div className="mb-4 mt-1">
                    <label
                      htmlFor="funding"
                      className="block text-md font-semibold text-gray-900"
                    >
                      Funding Stage
                    </label>
                    <select
                      id="funding"
                      name="funding"
                      value={formData.funding}
                      onChange={handleChange}
                      className="bg-transparent mt-2 border border-gray-300 text-gray-900 text-md rounded-lg focus:ring-blue-500 focus:border-blue-500 block pr-10 p-1.5"
                    >
                      <option selected>Choose an option</option>
                      <option value="Pre-Seed">Pre-seed</option>
                      <option value="Seed">Seed</option>
                      <option value="Series A">Series A</option>
                      <option value="Series B">Series B</option>
                      <option value="Series B+">Series B+</option>
                      <option value="Series C+">Series C+</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="col-span-full">
                    <label
                      htmlFor="description"
                      className="block text-md font-semibold leading-6 text-gray-900"
                    >
                      Description
                    </label>
                    <div className="mb-2 flex space-x-2 items-center">
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById('description');
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = textarea.value;
                          const before = text.substring(0, start);
                          const selection = text.substring(start, end);
                          const after = text.substring(end);
                          const newText = before + '**' + selection + '**' + after;
                          setFormData({ ...formData, description: newText });
                          
                          // Set cursor position after the inserted text
                          setTimeout(() => {
                            textarea.focus();
                            const newPosition = end + 4; // 4 is the length of the added '**' tags
                            textarea.setSelectionRange(newPosition, newPosition);
                          }, 0);
                        }}
                        className="p-1 rounded hover:bg-gray-100 text-gray-700 border border-gray-300"
                        title="Bold"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                          <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById('description');
                          const start = textarea.selectionStart;
                          const text = textarea.value;
                          const before = text.substring(0, start);
                          const after = text.substring(start);
                          
                          // Add a bullet point at the beginning of the line
                          let newText;
                          if (before.endsWith('\n') || before === '') {
                            newText = before + '• ' + after;
                          } else {
                            newText = before + '\n• ' + after;
                          }
                          
                          setFormData({ ...formData, description: newText });
                          
                          // Set cursor position after the bullet point
                          setTimeout(() => {
                            textarea.focus();
                            const newPosition = start + (before.endsWith('\n') || before === '' ? 2 : 3);
                            textarea.setSelectionRange(newPosition, newPosition);
                          }, 0);
                        }}
                        className="p-1 rounded hover:bg-gray-100 text-gray-700 border border-gray-300"
                        title="Bullet Point"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="8" y1="6" x2="21" y2="6"></line>
                          <line x1="8" y1="12" x2="21" y2="12"></line>
                          <line x1="8" y1="18" x2="21" y2="18"></line>
                          <line x1="3" y1="6" x2="3.01" y2="6"></line>
                          <line x1="3" y1="12" x2="3.01" y2="12"></line>
                          <line x1="3" y1="18" x2="3.01" y2="18"></line>
                        </svg>
                      </button>
                      <span className="text-sm text-gray-500">Add basic formatting to your description</span>
                    </div>
                    <textarea
                      id="description"
                      name="description"
                      rows="6"
                      className="block w-full pl-2 rounded-lg bg-transparent border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-md sm:leading-6 p-3"
                      placeholder="A short description of your company!"
                      value={formData.description}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          {/* Compact Team Section - Only show for non-internal users */}
          {currentUser && !currentUser.isLegacyLogin && (
            <div className="mt-8 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Logged in as</h3>
              <div className="space-y-4">
                {/* Only display the current user */}
                <div key={currentUser.id || currentUser.email} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[#02B491] rounded-full flex items-center justify-center">
                      <span className="text-white font-medium">
                        {(currentUser.name?.[0] || currentUser.email?.[0] || '?').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {currentUser.name || currentUser.email} (You)
                      </p>
                      <p className="text-sm text-gray-500">{currentUser.email}</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">
                    {currentUser.role}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* All Team Members Section */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members & Hiring Leads</h3>
            
            {teamLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5 text-[#02B491]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  <span className="text-gray-500">Loading team members...</span>
                </div>
              </div>
            ) : teamError ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error loading team members</h3>
                    <p className="text-sm text-red-700 mt-1">{teamError}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 md:space-y-4"> {/* mobile-only tweak: tighter spacing on mobile */}
                {teamMembers.length === 0 ? (
                  <div className="text-center py-6 md:py-8"> {/* mobile-only tweak: less padding on mobile */}
                    <svg className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"> {/* mobile-only tweak: smaller icon on mobile */}
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No team members found</h3>
                    <p className="mt-1 text-sm text-gray-500 px-4"> {/* mobile-only tweak: side padding for better readability */}
                      Invite team members to collaborate on your company account.
                    </p>
                  </div>
                ) : (
                  <>
                    {teamMembers.map((member) => (
                  <div key={member.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 md:p-4 bg-gray-50 rounded-lg space-y-3 sm:space-y-0"> {/* mobile-only tweak: stack vertically on mobile, horizontal on sm+, responsive padding */}
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#02B491] rounded-full flex items-center justify-center flex-shrink-0"> {/* mobile-only tweak: prevent shrinking */}
                        <span className="text-white font-medium">
                          {member.firstName?.[0] || member.email?.[0] || '?'}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1"> {/* mobile-only tweak: allow text to wrap properly */}
                        <p className="text-sm font-medium text-gray-900 break-words"> {/* mobile-only tweak: allow line breaks */}
                          {member.firstName && member.lastName 
                            ? `${member.firstName} ${member.lastName}`
                            : member.email
                          }
                          {currentUser?.email === member.email && (
                            <span className="text-[#02B491] ml-2">(You)</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500 break-all">{member.email}</p> {/* mobile-only tweak: break long emails */}
                      </div>
                    </div>
                    <div className="flex items-center justify-end sm:justify-start space-x-2 flex-shrink-0"> {/* mobile-only tweak: align right on mobile, prevent shrinking */}
                      <span className="px-2 py-1 bg-white text-gray-700 rounded-md text-xs font-medium border border-gray-200">
                        {member.role || 'Team Member'}
                      </span>
                      {currentUser?.email === member.email && (
                        <span className="px-2 py-1 bg-[#02B491] text-white rounded-md text-xs font-medium">
                          Current User
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Invite Team Members Section */}
          <div id="invite-section" className="mt-8 border-t border-gray-200 pt-6">
              <div className="flex items-center mb-4 relative">
                <h3 className="text-lg font-medium text-gray-900">Invite New Team Members</h3>
                <div 
                  className="ml-2 relative"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <svg 
                    className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {showTooltip && (
                    <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg -top-2 left-6 transform -translate-y-full w-80">
                      <div className="relative">
                        Inviting a team member will give them access to your company account, allowing them to manage roles, invite candidates, view applications, and perform other company-related tasks.
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3"> {/* mobile-only tweak: stack vertically on mobile, horizontal on sm+ */}
                  <div className="w-full sm:w-1/3 sm:min-w-[120px]"> {/* mobile-only tweak: full width on mobile */}
                    <input
                      type="text"
                      placeholder="Enter full name"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      className="block w-full pl-3 pr-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491] sm:text-sm" /* mobile-only tweak: better touch padding */
                    />
                  </div>
                  <div className="w-full sm:flex-1"> {/* mobile-only tweak: full width on mobile */}
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="block w-full pl-3 pr-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491] sm:text-sm" /* mobile-only tweak: better touch padding */
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleInvite}
                    disabled={!inviteEmail.trim() || !inviteName.trim() || inviteLoading}
                    className={`w-full sm:w-auto px-4 py-3 bg-[#02B491] text-white rounded-md text-sm font-medium hover:bg-[#029c7a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center min-h-[44px]`} /* mobile-only tweak: full width on mobile, proper touch target height */
                  >
                    {inviteLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Invite'
                    )}
                  </button>
                </div>
              </div>

            </div>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-start gap-4 pb-10"> {/* mobile-only tweak: stack vertically on mobile, horizontal on sm+ */}
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-3 text-sm bg-[#02B491] text-white border-2 border-[#02B491] hover:bg-white hover:text-black rounded-lg font-medium min-h-[44px]" /* mobile-only tweak: full width on mobile, proper touch target */
            >
              Save and update
            </button>
            <button
              onClick={handleLogout}
              className="w-full sm:w-auto px-4 py-3 rounded-md border border-red-400 bg-white text-black text-sm hover:bg-red-400 hover:text-white min-h-[44px]" /* mobile-only tweak: full width on mobile, proper touch target */
            >
              Logout
            </button>
          </div>
        </form>
        {isModalOpen && (
          <EditProfilePictureModal
            onClose={closeModal}
            formData={formData}
            setFormData={setFormData}
            setFile={setFile}
            file={file}
            onFileUpload={handleFileUpload}
          />
        )}
        <SuccessNotification
          isVisible={successNotification.isVisible}
          message={successNotification.message}
          onClose={() => setSuccessNotification({ isVisible: false, message: "" })}
        />
        <ErrorNotification
          isVisible={errorNotification.isVisible}
          message={errorNotification.message}
          onClose={() => setErrorNotification({ isVisible: false, message: "" })}
        />
      </div>
    </div>
  );
}

export default EditCompanyProfile;
